/** Update the installed unpacked extension; success requires the new relay version. */
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { newerThan } from '../confirm/apply';
import {
  afterWrote, applyPack, canWriteFolder, clearWrote, loadFolder, pickFolder, wroteThisTab,
  type ApplyOut,
} from './applyExt';
import { EXT_MIN, extSeen, extZipUrl } from './permit';
import './extUpdate.css';

export function ExtWarn(): ReactElement | null {
  const [seen, setSeen] = useState(extSeen);
  const [latest, setLatest] = useState(EXT_MIN);
  const [folder, setFolder] = useState<string | null>(null);
  const [say, setSay] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [written, setWritten] = useState(wroteThisTab);
  const [checked, setChecked] = useState(false);
  const [failed, setFailed] = useState(false);
  const running = useRef(false);
  const tried = useRef('');
  const alive = useRef(true);
  const stale = seen !== null && newerThan(seen, latest);
  const done = written !== null && seen !== null && !newerThan(seen, written);

  useEffect(() => {
    if (!busy) return;
    const warn = (event: BeforeUnloadEvent): void => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  useEffect(() => {
    alive.current = true;
    const observe = new MutationObserver(() => setSeen(extSeen()));
    observe.observe(document.documentElement, { attributes: true, attributeFilter: ['data-forq-ext'] });
    void loadFolder().then((dir) => { if (alive.current) setFolder(dir?.name ?? null); });
    const check = async (): Promise<void> => {
      try {
        const res = await fetch(extZipUrl().replace(/\.zip$/, '.json'), {
          cache: 'no-store', signal: AbortSignal.timeout(10000),
        });
        if (!res.ok) return;
        const meta = await res.json() as { version?: unknown };
        if (typeof meta.version === 'string' && /^\d+\.\d+(?:\.\d+){0,2}$/.test(meta.version) && alive.current) {
          const version = meta.version;
          setLatest(newerThan(EXT_MIN, version) ? version : EXT_MIN);
        }
      } catch { /* The compiled minimum remains available offline. */ }
      finally { if (alive.current) setChecked(true); }
    };
    void check();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void check(); }, 60000);
    return () => { alive.current = false; observe.disconnect(); window.clearInterval(timer); };
  }, []);

  const run = async (mode: 'auto' | 'update' | 'choose'): Promise<void> => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setFailed(false);
    setSay(mode === 'choose' ? 'Выберите папку установленного FORQ…' : 'Проверяем доступ к папке…');
    try {
      // Open the chooser directly in the click, before IDB/network can consume activation.
      const dir = mode === 'choose' ? await pickFolder() : undefined;
      if (dir === null) { setSay('Выбор отменён. Файлы не изменены.'); return; }
      if (dir !== undefined) setFolder(dir.name);
      const progress = (message: string): void => { if (alive.current) setSay(message); };
      const out: ApplyOut = await applyPack(seen, mode !== 'auto', undefined, { dir, progress });
      if (!alive.current) return;
      if (!out.ok) {
        setSay(mode === 'auto' && out.why.startsWith('Выберите папку') ? null : out.why);
        setFailed(mode !== 'auto');
        return;
      }
      setWritten(out.version);
      const message = await afterWrote(out, extSeen);
      if (alive.current) { setSeen(extSeen()); setSay(message); }
    } catch (error) {
      if (alive.current) {
        setFailed(true);
        setSay(error instanceof Error ? error.message : 'Не удалось обновить. Проверьте доступ к папке и повторите.');
      }
    } finally {
      running.current = false;
      if (alive.current) setBusy(false);
    }
  };

  useEffect(() => {
    const key = `${seen}:${latest}`;
    if (!checked || !stale || !canWriteFolder() || running.current || tried.current === key || (written !== null && !newerThan(written, latest))) return;
    tried.current = key;
    void run('auto');
  }, [checked, stale, seen, latest, written]);

  if (!stale && !busy && !done && say === null) return null;
  if (seen === null && !busy && written === null) return null;
  return (
    <div className={`nx-ext-update${done && !stale ? ' nx-ext-update--done' : ''}`}>
      <span className="nx-ext-update__label">
        {done && !stale ? `✓ Расширение обновлено · ${seen}` : `Обновление FORQ · ${seen ?? 'нет связи'} → ${latest}`}
      </span>
      {done && !stale ? (
        <>
          <span className="nx-ext-update__message" role="status">Новая версия на связи. Расширение не переустанавливалось. Если на площадке нет ответа — обновите её вкладку.</span>
          <span className="nx-ext-update__actions"><button type="button" className="nx-ext-update__secondary" onClick={() => { clearWrote(); setWritten(null); setSay(null); }}>Готово</button></span>
        </>
      ) : <>
        <span className="nx-ext-update__message">
          {busy ? 'Не закрывайте вкладку до окончания обновления.' : !canWriteFolder()
            ? `Расширение ${seen ?? ''} устарело — нужна ${latest}: скачайте forq.ru/forq-extension.zip → edge://extensions → «Обновить»; в AdsPower — «Расширения» → заменить.`
            : folder ? `Папка: ${folder}. Файлы заменим на месте, настройки расширения сохранятся.` : 'Один раз выберите папку установленного FORQ с файлом manifest.json. Сохранённую папку используем для следующих обновлений.'}
        </span>
        <span className="nx-ext-update__actions">
          {canWriteFolder() ? <>
            <button type="button" className="nx-ext-update__button" disabled={busy} onClick={() => void run(folder ? 'update' : 'choose')}>
              {busy ? 'Обновляем…' : folder ? 'обновите расширение' : 'обновите расширение — выбрать папку'}
            </button>
            {folder && <button type="button" className="nx-ext-update__secondary" disabled={busy} onClick={() => void run('choose')}>Выбрать другую папку</button>}
          </> : <a className="nx-ext-update__button" href={extZipUrl()} download="forq-extension.zip">обновите расширение — скачать ZIP</a>}
          {written && !busy && !done && <button type="button" className="nx-ext-update__secondary" onClick={() => window.location.reload()}>Обновить вкладку FORQ</button>}
        </span>
        {say && <span className="nx-ext-update__message" role={failed ? 'alert' : 'status'} aria-live="polite">{say}</span>}
        <details className="nx-ext-update__help">
          <summary>Где папка и что делать, если не обновилось?</summary>
          <span>Нужна та папка, которую вы выбрали при установке через «Загрузить распакованное расширение», а не ZIP и не общая папка загрузок. Не удаляйте расширение из браузера.</span>
          <span>Если автоматическая перезагрузка не сработала: откройте chrome://extensions (Edge: edge://extensions), нажмите круговую стрелку на карточке FORQ, затем обновите вкладки FORQ и площадок.</span>
          {!canWriteFolder() && <span>Этот браузер не умеет записывать файлы с сайта. Скачайте ZIP, распакуйте поверх той же папки и перезагрузите расширение по инструкции выше.</span>}
          <span>Магазинная версия обновляется средствами Chrome; выбирать её папку здесь не нужно.</span>
        </details>
      </>}
    </div>
  );
}
